import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cx } from '../../../internal/cx.js';
const DEFAULT_STATUS_LABEL = {
    confirmed: 'Confirmed',
    tentative: 'Tentative',
    cancelled: 'Cancelled',
    declined: 'Declined',
};
/**
 * One event in any calendar view: a colour dot or accent bar, the title, the
 * time and its status. It is a button (or a link with `href`) whose accessible
 * name reads "title, time, status", so it can open the product's event detail
 * or popover.
 */
export const EventChip = forwardRef(function EventChip({ title, time, timeLabel, status = 'confirmed', statusLabel, variant = 'dot', color, detail, past = false, density = 'regular', asChild = false, href, target, rel, type = 'button', className, style, children, ...rest }, ref) {
    const label = statusLabel ?? DEFAULT_STATUS_LABEL[status];
    const showStatus = status !== 'confirmed';
    const hasTime = time !== undefined && time !== null && time !== false && time !== '';
    const styles = color ? { ['--mn-event-color']: color, ...style } : style;
    const text = (n) => typeof n === 'string' || typeof n === 'number' ? String(n) : null;
    // A plain-text name reads the same in every browser and screen reader; with
    // rich (non-string) content the name comes from the content instead.
    const parts = [
        text(title),
        timeLabel ?? (hasTime ? text(time) : ''),
        showStatus ? label : '',
        detail && variant !== 'dot' ? text(detail) : '',
    ];
    const computedName = parts.every((p) => p !== null)
        ? parts.filter(Boolean).join(', ')
        : undefined;
    const common = {
        'aria-label': computedName,
        ...rest,
        className: cx('mn-event-chip', className),
        style: styles,
        'data-variant': variant,
        'data-status': status,
        'data-past': past || undefined,
        'data-density': variant === 'block' ? density : undefined,
    };
    const content = (_jsxs(_Fragment, { children: [_jsx("span", { className: "mn-event-chip__marker", "aria-hidden": true }), _jsxs("span", { className: "mn-event-chip__body", children: [_jsx("span", { className: "mn-event-chip__title", children: title }), hasTime || timeLabel ? ' ' : null, hasTime ? (_jsx("span", { className: "mn-event-chip__time", "aria-hidden": timeLabel ? true : undefined, children: time })) : null, timeLabel ? _jsx("span", { className: "mn-visually-hidden", children: timeLabel }) : null, showStatus ? (_jsxs(_Fragment, { children: [' ', _jsx("span", { className: "mn-event-chip__status", children: label })] })) : null, detail && variant !== 'dot' ? (_jsxs(_Fragment, { children: [' ', _jsx("span", { className: "mn-event-chip__detail", children: detail })] })) : null] })] }));
    if (asChild) {
        return (_jsxs(Slot, { ref: ref, ...common, children: [_jsx(Slottable, { children: children }), content] }));
    }
    if (href !== undefined) {
        return (_jsx("a", { ref: ref, ...common, href: href, target: target, rel: rel, children: content }));
    }
    return (_jsx("button", { ref: ref, type: type, ...common, children: content }));
});
//# sourceMappingURL=event-chip.js.map